import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "./VideoPlayer.css";
import { countryToAudioLang, getUserLangFromAudioLang } from "../../utils/languageMap";

const VideoPlayer = ({ videoUrl, userLang = "en", country = "default", onProgress }) => {
  //console.log("🎬 VideoPlayer loading videoUrl:", videoUrl);


  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [selectedLang, setSelectedLang] = useState(""); 
  const [hasLoadedTracks, setHasLoadedTracks] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }

    setHasLoadedTracks(false);

    const videoElement = document.createElement("video");
    videoElement.className = "video-js vjs-default-skin";
    videoElement.setAttribute("controls", true);
    videoElement.setAttribute("playsinline", true);

    const source = document.createElement("source");
    source.src = videoUrl;
    source.type = "application/x-mpegURL";
    videoElement.appendChild(source);

    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(videoElement);

    const player = videojs(videoElement, {
      responsive: true,
      fluid: true,
    });

    playerRef.current = player;

    //adding here tracking for being watched
    let lastSentAt = 0;

    const sendProgress = () => {
      if(!onProgress) return;

      const currentTime = Math.floor(player.currentTime() || 0);
      const duration = Math.floor(player.duration() || 0);

      if (!duration) return;

      const now = Date.now();
      //sending in every 30 secs 
      if (now - lastSentAt < 30000) return;
      lastSentAt = now;

      onProgress({ watchedSeconds: currentTime,
      videoDuration: duration
      });
    };

    player.on("timeupdate", sendProgress);

    player.on("ended", () => {
      const duration = Math.floor(player.duration() || 0);
      onProgress?.({
        watchedSeconds: duration,
        videoDuration: duration
      });
    });


    player.ready(() => {
      const defaultAudioLang = countryToAudioLang[country?.toUpperCase()] || "eng";

      const waitTracks = setInterval(() => {
        const tracks = player.audioTracks?.();
        if (tracks?.length) {
          clearInterval(waitTracks);
          setHasLoadedTracks(true);

          console.log("✅ Tracks ready → default switching to", defaultAudioLang);

          // Disable all first
          for (let i = 0; i < tracks.length; i++) {
            tracks[i].enabled = false;
          }

          // Enable correct track
          for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].language === defaultAudioLang) {
              tracks[i].enabled = true;
              console.log(`✅ Enabled track [${i}]: ${tracks[i].language}`);
              break;
            }
          }

          // Set selectedLang correctly for dropdown
          const dropdownLang = getUserLangFromAudioLang(defaultAudioLang);
          setSelectedLang(dropdownLang);
        }
      }, 300);
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoUrl, country]);

  useEffect(() => {
    if (!hasLoadedTracks || selectedLang === "") return;
    const player = playerRef.current;
    if (!player) return;

    const tracks = player.audioTracks?.();
    if (!tracks?.length) {
      console.warn("⚠️ No tracks found for language switch");
      return;
    }

    const audioLangCode = {
      en: "eng",
      es: "spa",
      fr: "fra",
      it: "ita",
      pt: "por",
      tr: "tur",
      cn: "chi",
    }[selectedLang] || "eng"; // fallback to English

    console.log("🔁 Switching language to", audioLangCode);

    for (let i = 0; i < tracks.length; i++) {
      tracks[i].enabled = false;
    }
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].language === audioLangCode) {
        tracks[i].enabled = true;
        console.log(`✅ Enabled track ${i}: ${tracks[i].language}`);
        break;
      }
    }
  }, [selectedLang, hasLoadedTracks]);

  

  return (
    <div className="video-wrapper" data-vjs-player>
      <div className="vjs-custom-lang-selector">
        <label htmlFor="lang-select">Lang: </label>
        <select
          id="lang-select"
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
        >
          <option value="en">EN</option>
          <option value="es">ES</option>
          <option value="fr">FR</option>
          <option value="it">IT</option>
          <option value="pt">PT</option>
          <option value="tr">TR</option>
          <option value="cn">CN</option>
        </select>
      </div>

      <div className="video-wrapper" ref={containerRef} />
    </div>
  );
};

export default VideoPlayer;
